'use client';

import {
	createLocalStorageBooleanStore,
	useWebStorageStore,
} from '@/globals/webStorage/useWebStorage';
import React from 'react';
import { EditorWidgetConfig } from './EditorWidget.client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { pointerEventsNone } from '@/uiPrimitives/utility/pointerEvents.css';
import { position } from '@/uiPrimitives/layout/position';
import dynamic from 'next/dynamic';
import { editorWidgetLoadingCSS } from './EditorWidget.css';
import { box } from '@/uiPrimitives/layout/box';
import { pile } from '@/uiPrimitives/layout/pile';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { unprintable } from '@/uiPrimitives/base/printable.css';

declare global {
	interface Window {
		dop?: {
			openEditorWidget?: () => void;
		};
	}
}

const LoadableEditorWidget = dynamic(
	() => import('./EditorWidget.client').catch(() => ({ default: () => null })),
	{
		loading: () => (
			<span
				className={classes(
					editorWidgetLoadingCSS,
					box({
						borderBlockEndStyle: 'solid',
						borderColor: 'positive',
						borderWidth: '4px',
						blockSize: '10 | betweenH2Sections',
					}),
				)}
			/>
		),
		ssr: false,
	},
);

export const store = createLocalStorageBooleanStore({
	key: 'editorWidgetOpened',
});

export const EditorWidgetClientLoader = (
	props: EditorWidgetConfig,
): React.ReactElement | null => {
	const [opened, setOpened] = useWebStorageStore(store, props.hasCMSPreview);

	const onClose = React.useCallback(() => {
		setOpened(false);
	}, [setOpened]);

	React.useEffect(() => {
		window.dop ??= {};
		window.dop.openEditorWidget = () => {
			setOpened(true);
		};

		return () => {
			delete window.dop?.openEditorWidget;
		};
	}, [setOpened]);

	return opened ? (
		<div
			className={classes(
				unprintable,
				pointerEventsNone,
				position({
					position: 'fixed',
					insetInlineEnd: '0',
					insetBlockStart: '0',
					blockSize: '100svb',
					inlineSize: '100%',
				}),
				pile({ alignItems: 'center', justifyItems: 'end' }),
				zIndex({ zIndex: 'loadingIndicator' }),
			)}
		>
			<div
				className={
					// A workaround for Chrome bug: https://issues.chromium.org/issues/40792788
					// The position fixed parent element will disappear behind the `scrollbar-gutter: stable`.
					// This is a work around to keep this element in the viewport.
					// This can happen when the flyout menu is opened or the page is very short.
					classes(
						pile({ alignItems: 'center', justifyItems: 'end' }),
						position({
							position: 'sticky',
							insetInlineEnd: '0',
							blockSize: '100%',
						}),
					)
				}
			>
				<LoadableEditorWidget {...props} onClose={onClose} />
			</div>
		</div>
	) : null;
};
